/**
 * WeatherObservationsCard - Record weather and sea conditions
 */

import React, { useState } from 'react';
import { Cloud, ChevronDown, ChevronUp, Plus } from 'lucide-react';

const WeatherObservationsCard = ({ 
  weatherObservations,
  setWeatherObservations,
  isExpanded,
  onToggle,
  onAddLogEntry,
  activeVoyage
}) => {
  const [weatherData, setWeatherData] = useState({
    time: new Date().toTimeString().slice(0, 5),
    windDirection: '',
    windSpeed: '',
    windForce: '',
    seaState: '',
    swellHeight: '',
    swellDirection: '',
    visibility: '',
    barometer: '',
    barometerTrend: 'steady',
    airTemp: '',
    seaTemp: '',
    cloudCover: '',
    precipitation: 'none',
    notes: ''
  });

  const beaufortScale = [
    { value: '0', label: 'Force 0 - Calm' },
    { value: '1', label: 'Force 1 - Light Air' },
    { value: '2', label: 'Force 2 - Light Breeze' },
    { value: '3', label: 'Force 3 - Gentle Breeze' },
    { value: '4', label: 'Force 4 - Moderate Breeze' },
    { value: '5', label: 'Force 5 - Fresh Breeze' },
    { value: '6', label: 'Force 6 - Strong Breeze' },
    { value: '7', label: 'Force 7 - Near Gale' },
    { value: '8', label: 'Force 8 - Gale' },
    { value: '9', label: 'Force 9 - Severe Gale' },
    { value: '10', label: 'Force 10 - Storm' },
    { value: '11', label: 'Force 11 - Violent Storm' },
    { value: '12', label: 'Force 12 - Hurricane' }
  ];

  const seaStates = [
    { value: '0', label: '0 - Calm (glassy)' },
    { value: '1', label: '1 - Calm (rippled)' },
    { value: '2', label: '2 - Smooth' },
    { value: '3', label: '3 - Slight' },
    { value: '4', label: '4 - Moderate' },
    { value: '5', label: '5 - Rough' },
    { value: '6', label: '6 - Very Rough' },
    { value: '7', label: '7 - High' },
    { value: '8', label: '8 - Very High' },
    { value: '9', label: '9 - Phenomenal' }
  ];

  const handleSaveObservation = () => {
    if (!weatherData.windDirection || !weatherData.windSpeed) {
      alert('Please enter at least wind direction and speed');
      return;
    }

    const observation = {
      ...weatherData,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };

    // Add to observations
    setWeatherObservations([...weatherObservations, observation]);

    // Create log entry
    const summary = `Wind: ${weatherData.windDirection}° ${weatherData.windSpeed}kts (Force ${weatherData.windForce}) • Sea State: ${weatherData.seaState} • Vis: ${weatherData.visibility || 'N/A'}`;
    
    const logEntry = {
      type: 'WEATHER',
      category: 'weather',
      summary: summary,
      details: observation,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    };

    onAddLogEntry(logEntry);

    // Reset form but keep some fields for convenience
    setWeatherData(prev => ({
      ...prev,
      time: new Date().toTimeString().slice(0, 5),
      notes: ''
    }));
  };

  const lastObservation = weatherObservations[weatherObservations.length - 1];

  return (
    <div className="rounded-lg border-2 bg-white border-gray-200">
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cloud className="h-5 w-5 text-gray-600" />
            <div>
              <h4 className="text-sm font-medium">Weather &amp; Sea Conditions</h4>
              <p className="text-xs text-gray-500">
                {lastObservation 
                  ? `Last: ${lastObservation.windDirection}° ${lastObservation.windSpeed}kts • Sea ${lastObservation.seaState}`
                  : 'No observations recorded'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {weatherObservations.length > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                {weatherObservations.length}
              </span>
            )}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            {/* Weather Form */}
            <div className="space-y-4">
              {/* Wind */}
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-2">Wind Conditions</h5>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Direction (°)</label>
                    <input
                      type="number"
                      value={weatherData.windDirection}
                      onChange={(e) => setWeatherData(prev => ({ ...prev, windDirection: e.target.value }))}
                      placeholder="000"
                      min="0"
                      max="360"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Speed (kts)</label>
                    <input
                      type="number"
                      value={weatherData.windSpeed}
                      onChange={(e) => setWeatherData(prev => ({ ...prev, windSpeed: e.target.value }))}
                      placeholder="0"
                      min="0"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Force</label>
                    <select
                      value={weatherData.windForce}
                      onChange={(e) => setWeatherData(prev => ({ ...prev, windForce: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select</option>
                      {beaufortScale.map(scale => (
                        <option key={scale.value} value={scale.value}>
                          {scale.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Sea Conditions */}
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-2">Sea Conditions</h5>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Sea State</label>
                    <select
                      value={weatherData.seaState}
                      onChange={(e) => setWeatherData(prev => ({ ...prev, seaState: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select</option>
                      {seaStates.map(state => (
                        <option key={state.value} value={state.value}>
                          {state.value}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Swell (m)</label>
                    <input
                      type="number"
                      value={weatherData.swellHeight}
                      onChange={(e) => setWeatherData(prev => ({ ...prev, swellHeight: e.target.value }))}
                      placeholder="0.0"
                      step="0.1"
                      min="0"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Direction (°)</label>
                    <input
                      type="number"
                      value={weatherData.swellDirection}
                      onChange={(e) => setWeatherData(prev => ({ ...prev, swellDirection: e.target.value }))}
                      placeholder="000"
                      min="0"
                      max="360"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Visibility & Barometer */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Visibility</label>
                  <select
                    value={weatherData.visibility}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, visibility: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="&lt;50m">Less than 50m</option>
                    <option value="50-200m">50-200m</option>
                    <option value="200-500m">200-500m</option>
                    <option value="500-1000m">500-1000m</option>
                    <option value="1-2km">1-2 km</option>
                    <option value="2-5km">2-5 km</option>
                    <option value="5-10km">5-10 km</option>
                    <option value="&gt;10km">More than 10km</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Barometer</label>
                  <input
                    type="number"
                    value={weatherData.barometer}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, barometer: e.target.value }))}
                    placeholder="1013"
                    min="900"
                    max="1100"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Trend</label>
                  <select
                    value={weatherData.barometerTrend}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, barometerTrend: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="rising">Rising</option>
                    <option value="steady">Steady</option>
                    <option value="falling">Falling</option>
                  </select>
                </div>
              </div>

              {/* Temperature & Precipitation */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Air (°C)</label>
                  <input
                    type="number"
                    value={weatherData.airTemp}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, airTemp: e.target.value }))}
                    placeholder="20"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Sea (°C)</label>
                  <input
                    type="number"
                    value={weatherData.seaTemp}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, seaTemp: e.target.value }))}
                    placeholder="18"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Cloud</label>
                  <select
                    value={weatherData.cloudCover}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, cloudCover: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="0/8">Clear</option>
                    <option value="1-2/8">Few</option>
                    <option value="3-4/8">Scattered</option>
                    <option value="5-7/8">Broken</option>
                    <option value="8/8">Overcast</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Precip</label>
                  <select
                    value={weatherData.precipitation}
                    onChange={(e) => setWeatherData(prev => ({ ...prev, precipitation: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="drizzle">Drizzle</option>
                    <option value="rain">Rain</option>
                    <option value="heavy_rain">Heavy Rain</option>
                    <option value="snow">Snow</option>
                    <option value="hail">Hail</option>
                    <option value="fog">Fog</option>
                  </select>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveObservation}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Log Weather Observation
              </button>
            </div>

            {/* Recent Observations */}
            {weatherObservations.length > 0 && (
              <div className="border-t pt-4">
                <h5 className="text-xs font-medium text-gray-700 mb-2">Recent Observations</h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {weatherObservations.slice(-3).reverse().map((obs) => (
                    <div key={obs.id} className="bg-gray-50 p-2 rounded text-xs">
                      <div className="flex justify-between">
                        <span className="font-medium">{obs.time}</span>
                        <span>Wind: {obs.windDirection}° {obs.windSpeed}kts</span>
                      </div>
                      <div className="text-gray-600 mt-1">
                        Sea: {obs.seaState} • Vis: {obs.visibility} • Bar: {obs.barometer} {obs.barometerTrend}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherObservationsCard;